import { usePriceHistory } from '@ui/hooks/price/usePriceHistory';
import { queryClient } from '@ui/lib/reactQuery';
import { Bar, LibrarySymbolInfo, SubscribeBarsCallback } from 'trading-view';

import { GetHistoryParams } from '../history/getHistory';
import { SupportedResolution } from '../types';
import { getNextBarTime } from './getNextBarTime';
import { usePriceStream } from './usePriceStream';

type Handler = {
  id: string;
  lastBar?: Bar;
  callback: SubscribeBarsCallback;
  resolution: SupportedResolution;
  onResetCacheNeededCallback: () => void;
  isIgnoreLastBar?: boolean;
};

type Subscription = {
  /**
   * per resolution
   */
  handlers: Handler[];
};

type StreamResponse = {
  id: string; // 'Crypto.FRAX/USD', ticker
  p: number;
  t: number;
  f: string;
  s: number;
};
const channelToSubscription = new Map<string, Subscription>();
let latestTicker: string | undefined = undefined;
let isStreaming = false;

export function startStreaming(retries = 3, delay = 3000, hardStart = false) {
  // use react query to maintain single instance of reader
  let reader: Promise<ReadableStreamDefaultReader<Uint8Array> | undefined>;
  if (hardStart) {
    // restart a new event stream connection
    reader = queryClient.fetchQuery({
      queryKey: usePriceStream.getKey(),
      queryFn: usePriceStream.fetcher,
    });
  } else {
    reader = queryClient.ensureQueryData({
      queryKey: usePriceStream.getKey(),
      queryFn: usePriceStream.fetcher,
    });
  }
  reader
    .then((reader) => {
      if (!reader) {
        attemptReconnect(retries, delay);

        return;
      }

      /// on reconnect, update from latestBarTime to now
      if (latestTicker) {
        const sub = channelToSubscription.get(latestTicker);
        if (sub) {
          // Reconnect Logic
          for (const handler of sub.handlers) {
            const lastBar = handler.lastBar;
            if (!lastBar) continue;

            const lastBarTimeSec = Math.floor(lastBar.time / 1000);
            const currentTimeSec = Math.floor(Date.now() / 1000);

            // If gap is too large (> 2h), reset cache
            if (currentTimeSec - lastBarTimeSec > 2 * 60 * 60) {
              try {
                handler.onResetCacheNeededCallback();
              } catch (e) {
                console.error('Error onResetCacheNeededCallback:', e);
              }
              continue;
            }

            // Gap filling: fetch missing bars
            const params: GetHistoryParams = {
              ticker: latestTicker,
              from: lastBarTimeSec,
              to: currentTimeSec,
              resolution: handler.resolution,
            };

            const queryKey = usePriceHistory.getQueryKey(params);
            const queryFn = usePriceHistory.getQueryFn(params);

            queryClient
              .fetchQuery({
                queryKey,
                queryFn,
              })
              .then((bars) => {
                bars.forEach((bar) => {
                  if (bar.time >= (handler.lastBar?.time || 0)) {
                    handler.lastBar = bar;
                    handler.callback(bar);
                  }
                });
              })
              .catch((e) => {
                console.error('Gap filling failed:', e);
              });
          }
        }
        // const params: GetHistoryParams = {
        //   ticker: symbolInfo.ticker,
        //   from: currentFrom,
        //   to: currentTo,
        //   resolution: resolution as SupportedResolution,
        // };
        // const queryKey = usePriceHistory.getQueryKey(params);
        // const queryFn = usePriceHistory.getQueryFn(params);
        // promises.push(
        //   queryClient.ensureQueryData({
        //     queryKey,
        //     queryFn,
        //   }),
        // );
      }

      isStreaming = true;
      streamData(reader, retries, delay);
    })
    .catch((error) => {
      console.error(
        '[stream] Error fetching from the streaming endpoint:',
        error,
      );

      attemptReconnect(retries, delay);
    });
}

function attemptReconnect(retriesLeft: number, delay: number) {
  if (retriesLeft > 0) {
    setTimeout(() => {
      startStreaming(retriesLeft - 1, delay, true);
    }, delay);
  } else {
    console.error('[stream] Maximum reconnection attempts reached.');
  }
}
function streamData(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  retries = 100000,
  delay = 3000,
) {
  reader
    .read()
    .then(({ value, done }) => {
      if (done) {
        attemptReconnect(retries, delay);
        
return;
      }

      // Assuming the streaming data is separated by line breaks
      const dataStrings = new TextDecoder().decode(value).split('\n');
      dataStrings.forEach((dataString) => {
        const trimmedDataString = dataString.trim();
        if (trimmedDataString) {
          try {
            var jsonData = JSON.parse(trimmedDataString);
            handleStreamingData(jsonData);
          } catch (e) {
            // some partial json like this: ,"f":"t","s":0}
            // console.error('Error parsing JSON:', e);
          }
        }
      });

      streamData(reader); // Continue processing the stream
    })
    .catch((error) => {
      console.error('[stream] Error reading from stream:', error);
      attemptReconnect(retries, delay);
    });
}

function handleStreamingData(latestPriceData: StreamResponse) {
  const { id: channelString, p: latestPrice, t } = latestPriceData;
  const latestTime = t * 1000; // Multiplying by 1000 to get milliseconds
  const subscriptionItem = channelToSubscription.get(channelString);

  if (!subscriptionItem) {
    return;
  }
  // Send data to every resolution subscriber of that symbol
  subscriptionItem.handlers.forEach((handler) => {
    if ('isIgnoreLastBar' in handler && handler.isIgnoreLastBar) {
      handler.callback({
        time: 0,
        open: latestPrice,
        high: latestPrice,
        low: latestPrice,
        close: latestPrice,
      });

      return;
    }
    const lastBar = handler.lastBar;
    if (!lastBar) return;

    const newBarTime = getNextBarTime(lastBar.time, handler.resolution);

    let bar: Bar;
    if (latestTime >= newBarTime) {
      // latest price timestamp is after or equal the next bar time,
      // use the next bar time with latest price
      bar = {
        time: newBarTime,
        open: latestPrice,
        high: latestPrice,
        low: latestPrice,
        close: latestPrice,
      };
    } else {
      // latest price timestamp is before the next bar time,
      // use the last valid bar time with latest price h,l,c
      bar = {
        ...lastBar,
        high: Math.max(lastBar.high, latestPrice),
        low: Math.min(lastBar.low, latestPrice),
        close: latestPrice,
      };
    }
    // update lastBar info
    handler.lastBar = bar;

    handler.callback(bar);
  });

  channelToSubscription.set(channelString, subscriptionItem);
}

export function subscribeOnStream(
  symbolInfo: Pick<LibrarySymbolInfo, 'ticker'>,
  resolution: SupportedResolution,
  onRealtimeCallback: SubscribeBarsCallback,
  subscriberUID: string, // "Crypto.ETH/USD_#_240"
  onResetCacheNeededCallback: () => void,
  lastBar?: Bar,
  isIgnoreLastBar: boolean = false,
) {
  const channelString = symbolInfo.ticker;
  latestTicker = channelString;
  if (!channelString) return;
  let handler: Handler = {
    id: subscriberUID,
    callback: onRealtimeCallback,
    lastBar,
    resolution,
    isIgnoreLastBar,
    onResetCacheNeededCallback,
  };
  closeStreamOnIdle();
  const existingSubscription = channelToSubscription.get(channelString);

  /**
   * - all token prices should share same event stream
   * - add channel for a token if not exist
   * - add/remove handler for different resolutions
   */

  if (existingSubscription) {
    const existHandler = !!existingSubscription.handlers.find(
      (v) => v.id === subscriberUID,
    );
    if (!existHandler) {
      existingSubscription.handlers.push(handler);
      channelToSubscription.set(channelString, existingSubscription);
    }
  } else {
    const subscriptionItem: Subscription = {
      handlers: [handler],
    };
    channelToSubscription.set(channelString, subscriptionItem);
  }

  //  all token prices should share same event stream
  if (!isStreaming) {
    startStreaming();
  }
}

export function unsubscribeFromStream(subscriberUID: string) {
  // Find a subscription with id === subscriberUID
  for (const channelString of channelToSubscription.keys()) {
    const subscriptionItem = channelToSubscription.get(channelString);
    if (!subscriptionItem) continue;

    // remove handler on unsub
    subscriptionItem.handlers = subscriptionItem.handlers.filter(
      (handler) => handler.id !== subscriberUID,
    );
    channelToSubscription.set(channelString, subscriptionItem);
  }
}

async function stopStreaming() {
  const reader = await queryClient.getQueryData<
    ReturnType<typeof usePriceStream.fetcher>
  >(usePriceStream.getKey());
  reader?.cancel('manual stop');
  queryClient.removeQueries({ queryKey: usePriceStream.getKey() });
  isStreaming = false;
}

let closeStreamInterval: NodeJS.Timeout;
/**
 * periodically check if there are any active handlers, close the stream if none
 */
function closeStreamOnIdle() {
  clearInterval(closeStreamInterval);
  closeStreamInterval = setInterval(() => {
    let handlerCount = 0;
    channelToSubscription.forEach((subscription) => {
      handlerCount += subscription.handlers.length;
    });

    if (handlerCount === 0) {
      stopStreaming();
      clearInterval(closeStreamInterval);
    }
  }, 5000);
}
